import type { CrucibleInviteStatus } from '@sorrem/shared';
import { useSelectedCharacterId } from '@sorrem-client/ui/hooks';
import { useGetCrucibleInviteByCodeQuery, useGetCrucibleInviteStatusQuery } from '@sorrem-client/ui/store';

/** Why an invite link's crucible can't be joined, as the player reads it; null when it can. */
export function describeInviteStatus(status: CrucibleInviteStatus): string | null {
    const name = status.displayName ?? 'This Crucible';
    switch (status.state) {
        case 'joinable':
            return null;
        case 'full':
            return `${name} is full.`;
        case 'started':
            return `${name} has already begun; it can't be joined now.`;
        case 'ended':
            return `${name} is over.`;
        case 'private':
            return 'This Crucible is a solo run; nobody else can join it.';
        case 'not-found':
            return 'That Crucible no longer exists.';
        case 'already-member':
            return `You're already in ${name}.`;
    }
}

/** Why the crucible an invite code names can't be joined, or that it names none; null when it can be. */
export function describeInviteCodeStatus(inviteCode: string, status: CrucibleInviteStatus): string | null {
    return status.state === 'not-found' ? `No Crucible has the invite code ${inviteCode}.` : describeInviteStatus(status);
}

/**
 * For an invite link (#216) to a crucible the open list doesn't show: why it
 * can't be joined. Asks only when the link's crucible isn't listed, since a
 * listed one can simply be entered.
 */
export function useCrucibleInvite(linkedCrucibleId: string | null, isListed: boolean): { unavailableNotice: string | null } {
    const shouldAsk = linkedCrucibleId !== null && !isListed;
    const { data } = useGetCrucibleInviteStatusQuery(linkedCrucibleId ?? '', { skip: !shouldAsk });
    if (!shouldAsk || data === undefined) {
        return { unavailableNotice: null };
    }
    return { unavailableNotice: describeInviteStatus(data) };
}

export interface CrucibleInviteCodeLookup {
    /** The crucible the code names, once found; null until then, or when it names none. */
    crucibleId: string | null;
    /** Why that crucible can't be joined, or that the code names none; null otherwise. */
    unavailableNotice: string | null;
}

/**
 * Finds the crucible an invite code names (`/character/crucibles/join/:inviteCode`).
 * One the selected character is already in stays listed, so Enter takes it back in.
 */
export function useCrucibleInviteCode(inviteCode: string | null): CrucibleInviteCodeLookup {
    const { selectedCharacterId } = useSelectedCharacterId();
    const { data } = useGetCrucibleInviteByCodeQuery({ inviteCode: inviteCode ?? '', characterId: selectedCharacterId }, { skip: inviteCode === null });
    if (inviteCode === null || data === undefined) {
        return { crucibleId: null, unavailableNotice: null };
    }
    return { crucibleId: data.crucibleId ?? null, unavailableNotice: describeInviteCodeStatus(inviteCode, data) };
}
